import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    HMSRoomProvider,
    useHMSActions,
    useHMSStore,
    useHMSNotifications,
    selectPeers,
    selectRemotePeers,
    selectIsConnectedToRoom,
    selectLocalPeer,
    selectPeerScreenSharing,
    selectScreenShareByPeerID,
    selectIsPeerAudioEnabled,
    selectIsPeerVideoEnabled,
    selectRoomState,
    useVideo,
    useScreenShare,
    useAutoplayError,
    HMSNotificationTypes,
} from "@100mslive/react-sdk";
import { Tldraw, useEditor } from "tldraw";
import "tldraw/tldraw.css";
import axios from "axios";
import { toast } from "sonner";
import {
    Loader2, Mic, MicOff, Video, VideoOff, PhoneOff, PenLine,
    Users, AlertTriangle, Clock, ArrowLeft, ClipboardCheck, X,
    MonitorUp, MonitorOff, Volume2, Wifi, WifiOff, UserCircle2,
} from "lucide-react";

/**
 * V158 — Native In-Platform Live Classroom.
 *
 * Route: /portal/{party}/{token}/session/{schedule_id}/live
 * Standalone (no marketing layout, no navbar / footer / SEO graph).
 *
 * Composition:
 *   - 100ms React SDK for A/V (parent + tutor roles minted server-side)
 *   - tldraw for the shared whiteboard (locally rendered; snapshot PNG
 *     posted to backend when the tutor ends the class)
 *   - Server-authoritative timer from webhook data; local timer only for UX
 *   - Everything remains inside anandtutorials.com — parents & tutors never
 *     see each other's contact info, and never leave the platform
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API });

function LiveClassroomInner() {
    const { party, token, schedule_id } = useParams();
    const navigate = useNavigate();
    const isTutor = party === "tutor";

    const [videoEnabled, setVideoEnabled] = useState(true); // true = HMS mode; false = fallback mode
    const [hmsConfig, setHmsConfig] = useState(null);
    const [joining, setJoining] = useState(false);
    const [joinError, setJoinError] = useState("");
    const [elapsedSec, setElapsedSec] = useState(0);
    const [endingClass, setEndingClass] = useState(false);
    const [showWhiteboard, setShowWhiteboard] = useState(true);
    // V163 — Mobile-only segmented control. On phones the video sidebar +
    // whiteboard can't share the viewport comfortably, so we let the user
    // pick one panel at a time (`video` | `board`). Desktop (md+) ignores
    // this — both panels remain side-by-side.
    const [mobilePane, setMobilePane] = useState("board");
    const [editorRef, setEditorRef] = useState(null);
    // Post-class report modal state (tutor only).
    const [showReport, setShowReport] = useState(false);

    const actions = useHMSActions();
    const isConnected = useHMSStore(selectIsConnectedToRoom);
    const roomState = useHMSStore(selectRoomState);
    const peers = useHMSStore(selectPeers);
    const remotePeers = useHMSStore(selectRemotePeers);
    const localPeer = useHMSStore(selectLocalPeer);
    const peerSharingScreen = useHMSStore(selectPeerScreenSharing);
    const notifications = useHMSNotifications();
    const startAtRef = useRef(null);

    // Screen share — provided by 100ms SDK, handles track replacement + signalling.
    const { amIScreenSharing, toggleScreenShare } = useScreenShare((err) => {
        // getDisplayMedia dismissed by user is NotAllowedError — don't scream at them.
        if (err?.message && !/dismissed|cancel|NotAllowedError/i.test(err.message)) {
            toast.error("Could not start screen share: " + err.message);
        }
    });

    // Autoplay unblock — mobile browsers block remote audio until user gesture.
    const { error: autoplayError, unblockAudio, resetError: resetAutoplayError } = useAutoplayError();

    // ---------------- Config + suppression of the marketing SEO graph.
    useEffect(() => {
        publicApi.get("/hms/config").then((r) => setHmsConfig(r.data)).catch(() => setHmsConfig({ enabled: false }));
        const existing = document.querySelector('meta[name="robots"]');
        const prior = existing?.getAttribute("content") || null;
        let injected = null;
        if (existing) existing.setAttribute("content", "noindex, nofollow");
        else {
            injected = document.createElement("meta");
            injected.name = "robots";
            injected.content = "noindex, nofollow";
            document.head.appendChild(injected);
        }
        return () => {
            if (existing && prior !== null) existing.setAttribute("content", prior);
            injected?.parentNode?.removeChild(injected);
        };
    }, []);

    // ---------------- Local timer once we're connected.
    useEffect(() => {
        if (!isConnected) return;
        if (!startAtRef.current) startAtRef.current = Date.now();
        const iv = setInterval(() => {
            setElapsedSec(Math.floor((Date.now() - startAtRef.current) / 1000));
        }, 1000);
        return () => clearInterval(iv);
    }, [isConnected]);

    // ---------------- HMS notifications — auto-cleanup on room end + permission errors.
    useEffect(() => {
        if (!notifications) return;
        const n = notifications.notification;
        if (!n) return;
        if (n.type === HMSNotificationTypes.ROOM_ENDED) {
            toast("The tutor has ended the class.", { icon: "🏁" });
            setTimeout(() => { navigate(-1); }, 1200);
        }
        if (n.type === HMSNotificationTypes.ERROR) {
            const d = n.data || {};
            const code = d.code;
            // 3001/3002/3003 → camera/mic/display permission denied by user or OS
            if (code === 3001 || code === 3002) {
                toast.error("Camera / microphone permission denied. Please enable it in your browser settings and rejoin.");
            } else if (code === 3011) {
                toast.error("Screen share permission denied by the browser.");
            } else if (d.isTerminal) {
                toast.error("Connection lost: " + (d.message || "please rejoin"));
            }
        }
    }, [notifications, navigate]);

    // ---------------- Leave on unmount.
    useEffect(() => () => { actions.leave().catch(() => {}); }, [actions]);

    // ---------------- Join.
    const doJoin = useCallback(async () => {
        setJoining(true);
        setJoinError("");
        try {
            const r = await publicApi.post(`/portal/${party}/${token}/session/${schedule_id}/classroom/join`);
            const { auth_token, user_name } = r.data;
            await actions.join({
                userName: user_name || party,
                authToken: auth_token,
                settings: {
                    isAudioMuted: false,
                    isVideoMuted: false,
                },
                rememberDeviceSelection: true,
            });
            // Explicitly enable local tracks after join — some mobile browsers
            // decline to publish until an explicit setLocal*Enabled call.
            try { await actions.setLocalAudioEnabled(true); } catch (_e) {}
            try { await actions.setLocalVideoEnabled(true); } catch (_e) {}
        } catch (e) {
            // Permission errors come back as HMSException from the SDK.
            const errMsg = e?.message || "";
            if (/permission|NotAllowed|denied/i.test(errMsg)) {
                setJoinError("Camera / microphone permission denied. Please allow access in your browser and rejoin.");
                setJoining(false);
                return;
            }
            const detail = e?.response?.data?.detail;
            const code = typeof detail === "object" ? detail.code : null;
            if (code === "video_not_configured") {
                setJoinError("Live video is not yet enabled by the admin. Please use the meeting link from the class card, or contact Anand Tutorials.");
            } else if (typeof detail === "string") {
                setJoinError(detail);
            } else {
                setJoinError("Could not join the classroom. Please retry in a moment.");
            }
        } finally {
            setJoining(false);
        }
    }, [party, token, schedule_id, actions]);

    // Auto-attempt join when config resolves.
    useEffect(() => {
        if (!hmsConfig) return;
        if (hmsConfig.enabled === false) {
            setVideoEnabled(false);
            return;
        }
        if (!isConnected && !joining && !joinError) doJoin();
    }, [hmsConfig, isConnected, joining, joinError, doJoin]);

    // ---------------- Whiteboard snapshot on end (tutor only).
    const captureWhiteboardSnapshot = useCallback(async () => {
        if (!editorRef) return null;
        try {
            const shapes = editorRef.getCurrentPageShapes();
            const ids = shapes.map((s) => s.id);
            if (ids.length === 0) return null;
            const { blob } = await editorRef.toImage(ids, { format: "png", background: true, scale: 1 });
            if (!blob) return null;
            const buf = await blob.arrayBuffer();
            const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
            return `data:image/png;base64,${b64}`;
        } catch (e) { return null; }
    }, [editorRef]);

    // V158.1 — Tutor clicks "End Class" → opens the report form. Submit is
    // the single finalise step that (a) captures whiteboard, (b) submits the
    // class report + attendance (which increments the completed_sessions
    // counter server-side), (c) ends the 100ms room, (d) leaves the SDK,
    // (e) navigates back to the portal.
    const submitReportAndEnd = useCallback(async (form) => {
        if (!isTutor) return;
        setEndingClass(true);
        try {
            // 1. Whiteboard snapshot (best-effort).
            const dataUrl = await captureWhiteboardSnapshot();
            if (dataUrl) {
                try {
                    await publicApi.post(
                        `/portal/tutor/${token}/session/${schedule_id}/whiteboard-snapshot`,
                        { data_url: dataUrl },
                    );
                } catch (_e) { /* non-fatal */ }
            }
            // 2. Report + attendance — must-not-fail step.
            try {
                await publicApi.post(
                    `/portal/tutor/${token}/session/${schedule_id}/complete`,
                    form,
                );
            } catch (e) {
                toast.error("Could not save the class report. Please retry.");
                setEndingClass(false);
                return;
            }
            // 3. End the 100ms room (best-effort; may already be closed).
            try {
                await publicApi.post(`/portal/tutor/${token}/session/${schedule_id}/classroom/end`);
            } catch (_e) { /* non-fatal */ }
            // 4. Leave + navigate.
            try { await actions.leave(); } catch (_e) {}
            setShowReport(false);
            toast.success("Class report saved. Session completed.");
            setTimeout(() => navigate(-1), 700);
        } finally {
            setEndingClass(false);
        }
    }, [isTutor, captureWhiteboardSnapshot, token, schedule_id, actions, navigate]);

    const leaveOnly = useCallback(async () => {
        try { await actions.leave(); } catch (_e) {}
        navigate(-1);
    }, [actions, navigate]);

    const toggleAudio = async () => {
        const enabled = localPeer?.audioTrack ? !localPeer?.audioTrack?.enabled : true;
        try { await actions.setLocalAudioEnabled(enabled); } catch (_e) {}
    };
    const toggleVideo = async () => {
        const enabled = localPeer?.videoTrack ? !localPeer?.videoTrack?.enabled : true;
        try { await actions.setLocalVideoEnabled(enabled); } catch (_e) {}
    };
    const handleToggleScreenShare = async () => {
        if (!toggleScreenShare) {
            toast.error("Screen share is not permitted for this role.");
            return;
        }
        try {
            await toggleScreenShare({ audioOnly: false, videoOnly: false });
        } catch (e) {
            // user cancelled — swallow quietly
        }
    };

    const isVideoOn = !!localPeer?.videoTrack?.enabled;
    const isAudioOn = !!localPeer?.audioTrack?.enabled;

    // Pick the "stage" peer — the first remote peer. For 1:1 classrooms there
    // is only ever one. We never put the local peer on the main stage: the
    // local user's self-view lives in a dedicated small PIP so they can
    // tell their own feed apart from the other participant's.
    const stagePeer = remotePeers?.[0] || null;
    // Connection status for the small indicator chip.
    const connStatus = isConnected
        ? "connected"
        : (joining || roomState === "Connecting" || roomState === "Preview")
            ? "connecting"
            : "disconnected";

    if (!hmsConfig) {
        return <FullScreenSpinner label="Preparing classroom…" />;
    }
    if (!videoEnabled || joinError) {
        return (
            <>
                <FallbackNotice
                    message={joinError || "Live video classroom is not yet activated."}
                    onBack={() => navigate(-1)}
                    isTutor={isTutor}
                    onSubmitReport={() => setShowReport(true)}
                />
                {isTutor && showReport && (
                    <ClassReportModal
                        elapsedSec={0}
                        submitting={endingClass}
                        onCancel={() => setShowReport(false)}
                        onSubmit={submitReportAndEnd}
                    />
                )}
            </>
        );
    }

    return (
        <div className="min-h-screen bg-[#0B1220] text-white flex flex-col classroom-mobile" data-testid={`live-classroom-${party}`}>
            {/* Header */}
            <header className="border-b border-white/10 px-3 sm:px-6 py-2.5 flex items-center justify-between gap-3 shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                    <button onClick={leaveOnly} className="p-2 -ml-1 rounded hover:bg-white/10 min-h-[40px] min-w-[40px] flex items-center justify-center" data-testid="live-back-btn" data-cm-compact aria-label="Back to portal">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold">Anand Tutorials · Live Class</div>
                        <div className="text-xs text-slate-300 truncate">{isTutor ? "Tutor session" : "Your class"} · Session #{schedule_id?.slice(0, 6)}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <ConnectionChip status={connStatus} />
                    <span className="inline-flex items-center gap-1 text-xs text-slate-300 bg-white/10 rounded-full px-2.5 py-1" data-testid="live-elapsed">
                        <Clock className="w-3.5 h-3.5" /> {formatDuration(elapsedSec)}
                    </span>
                    <span className="hidden sm:inline-flex items-center gap-1 text-xs text-slate-300 bg-white/10 rounded-full px-2.5 py-1">
                        <Users className="w-3.5 h-3.5" /> {peers.length}
                    </span>
                </div>
            </header>

            {/* Autoplay-blocked banner — mobile browsers block remote audio until user tap. */}
            {autoplayError && (
                <button
                    type="button"
                    onClick={async () => { try { await unblockAudio(); } catch (_e) {} resetAutoplayError(); }}
                    data-testid="live-unblock-audio"
                    className="w-full bg-amber-500 text-black text-xs sm:text-sm font-semibold px-3 py-2 flex items-center justify-center gap-2 hover:bg-amber-400"
                >
                    <Volume2 className="w-4 h-4" />
                    Tap here to enable sound (your browser blocked audio autoplay)
                </button>
            )}

            {/* V163 — Mobile-only pane switcher (Video / Board). Hidden on md+. */}
            <div className="md:hidden border-b border-white/10 bg-[#0F172A] px-3 py-2 flex items-center gap-2 shrink-0">
                <button
                    type="button"
                    onClick={() => setMobilePane("video")}
                    data-testid="live-mobile-pane-video"
                    data-active={mobilePane === "video" ? "true" : "false"}
                    data-cm-compact
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition-colors ${
                        mobilePane === "video"
                            ? "bg-cyan-500/20 text-cyan-100 ring-1 ring-cyan-400"
                            : "bg-white/5 text-slate-300 hover:bg-white/10"
                    }`}
                >
                    <Video className="w-3.5 h-3.5" /> Video
                    <span className="text-[10px] opacity-70">· {peers.length}</span>
                </button>
                <button
                    type="button"
                    onClick={() => setMobilePane("board")}
                    data-testid="live-mobile-pane-board"
                    data-active={mobilePane === "board" ? "true" : "false"}
                    data-cm-compact
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition-colors ${
                        mobilePane === "board"
                            ? "bg-cyan-500/20 text-cyan-100 ring-1 ring-cyan-400"
                            : "bg-white/5 text-slate-300 hover:bg-white/10"
                    }`}
                >
                    <PenLine className="w-3.5 h-3.5" /> Whiteboard
                </button>
            </div>

            {/* Main area — video sidebar + whiteboard */}
            <main className="flex-1 flex flex-col md:flex-row overflow-hidden">
                {/* Video area — stage layout.
                    - MAIN STAGE: either the active screen share, or the remote peer's camera.
                    - PIP (small): local self-view, muted, mirrored so it feels natural.
                    - This layout ensures the user NEVER sees their own feed on the main stage,
                      and the remote participant's feed is identified by stable peer.id — never
                      by array index or DOM order.
                   Full-screen on mobile when pane=video, left column on md+. */}
                <aside
                    className={`${mobilePane === "video" ? "flex" : "hidden"} md:flex flex-col md:w-80 lg:w-96 border-r border-white/10 bg-[#0F172A] p-2 sm:p-3 gap-2 overflow-y-auto ${showWhiteboard ? "" : "md:w-full"}`}
                    data-testid="live-video-stage"
                >
                    {!isConnected ? (
                        <div className="text-xs text-slate-400 p-4 text-center">
                            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                            Joining classroom…
                        </div>
                    ) : (
                        <>
                            {/* MAIN STAGE */}
                            <StageTile
                                remotePeer={stagePeer}
                                peerSharingScreen={peerSharingScreen}
                                amIScreenSharing={amIScreenSharing}
                                localPeer={localPeer}
                                isTutor={isTutor}
                            />
                            {/* LOCAL SELF-VIEW PIP */}
                            {localPeer && (
                                <SelfPreviewTile peer={localPeer} isTutor={isTutor} />
                            )}
                            <div className="text-[11px] text-slate-500 pt-2 border-t border-white/5">
                                {isTutor ? "Student / parent contact stays private." : "Tutor's contact stays private."}
                                <br />All coordination goes through Anand Tutorials.
                            </div>
                        </>
                    )}
                </aside>

                {/* Whiteboard — full-screen on mobile when pane=board, side-by-side on md+ */}
                {showWhiteboard && (
                    <section
                        className={`${mobilePane === "board" ? "flex" : "hidden"} md:flex flex-1 bg-white relative min-h-[300px] md:min-h-0`}
                        data-testid="live-whiteboard"
                    >
                        <Tldraw
                            persistenceKey={`at-classroom-${schedule_id}`}
                            onMount={(editor) => setEditorRef(editor)}
                            inferDarkMode={false}
                        />
                    </section>
                )}
            </main>

            {/* Controls */}
            <footer className="border-t border-white/10 px-2 sm:px-6 py-3 flex items-center justify-center gap-1.5 sm:gap-3 shrink-0 bg-[#0B1220] pb-[max(12px,env(safe-area-inset-bottom))] flex-wrap">
                <IconBtn onClick={toggleAudio} on={isAudioOn} onIcon={<Mic className="w-5 h-5 sm:w-4 sm:h-4" />} offIcon={<MicOff className="w-5 h-5 sm:w-4 sm:h-4" />} label="Mic" testid="live-mic" disabled={!isConnected} />
                <IconBtn onClick={toggleVideo} on={isVideoOn} onIcon={<Video className="w-5 h-5 sm:w-4 sm:h-4" />} offIcon={<VideoOff className="w-5 h-5 sm:w-4 sm:h-4" />} label="Camera" testid="live-cam" disabled={!isConnected} />
                <IconBtn
                    onClick={handleToggleScreenShare}
                    on={!amIScreenSharing}
                    onIcon={<MonitorUp className="w-5 h-5 sm:w-4 sm:h-4" />}
                    offIcon={<MonitorOff className="w-5 h-5 sm:w-4 sm:h-4" />}
                    label={amIScreenSharing ? "Stop share" : "Share"}
                    testid="live-share-screen"
                    disabled={!isConnected || !toggleScreenShare}
                />
                <IconBtn onClick={() => setShowWhiteboard((v) => !v)} on={showWhiteboard} onIcon={<PenLine className="w-5 h-5 sm:w-4 sm:h-4" />} offIcon={<PenLine className="w-5 h-5 sm:w-4 sm:h-4" />} label="Board" testid="live-board" />
                {isTutor ? (
                    <button
                        onClick={() => setShowReport(true)}
                        disabled={endingClass}
                        data-testid="live-end-class"
                        data-cm-live-ctl
                        data-cm-primary
                        className="inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white font-semibold text-sm px-4 py-2 rounded-full"
                    >
                        {endingClass ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneOff className="w-4 h-4" />}
                        End Class
                    </button>
                ) : (
                    <button
                        onClick={leaveOnly}
                        data-testid="live-leave"
                        data-cm-live-ctl
                        data-cm-primary
                        className="inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white font-semibold text-sm px-4 py-2 rounded-full"
                    >
                        <PhoneOff className="w-4 h-4" /> Leave
                    </button>
                )}
            </footer>

            {/* V158.1 — Post-class report modal (tutor only) */}
            {isTutor && showReport && (
                <ClassReportModal
                    elapsedSec={elapsedSec}
                    submitting={endingClass}
                    onCancel={() => setShowReport(false)}
                    onSubmit={submitReportAndEnd}
                />
            )}
        </div>
    );
}

// --------------------------------------------------------------------
// StageTile — the MAIN stage. Shows (in order of priority):
//   1. The active screen share (anyone's) — tracked by peerSharingScreen.
//   2. The remote peer's camera feed — identified by stable peer.id.
//   3. A placeholder "waiting for participant" / "camera off" state.
//
// Audio: 100ms SDK auto-plays remote audio via an internal AudioSink; we
// don't need an explicit <audio> tag. Local audio is NEVER played back
// locally (would cause feedback) — the SDK handles that too.
// --------------------------------------------------------------------
function StageTile({ remotePeer, peerSharingScreen, amIScreenSharing, localPeer, isTutor }) {
    // If anyone is sharing, that takes precedence on the main stage.
    const sharePeer = peerSharingScreen || null;
    // Remote screen share: show their shared video track on the stage.
    const remoteShareTrackId = useHMSStore(selectScreenShareByPeerID(sharePeer?.id))?.id;
    const remoteVideoOn = useHMSStore(selectIsPeerVideoEnabled(remotePeer?.id));
    const remoteAudioOn = useHMSStore(selectIsPeerAudioEnabled(remotePeer?.id));

    // ---- Screen share mode
    if (sharePeer && !sharePeer.isLocal && remoteShareTrackId) {
        return (
            <div className="flex flex-col gap-2">
                <ScreenShareView
                    trackId={remoteShareTrackId}
                    peerName={sharePeer.name}
                />
                {remotePeer && (
                    <SmallRemoteCamera peer={remotePeer} videoOn={remoteVideoOn} audioOn={remoteAudioOn} />
                )}
            </div>
        );
    }
    // ---- I'm the one screen-sharing — show a confirmation card + remote camera
    if (amIScreenSharing) {
        return (
            <div className="flex flex-col gap-2">
                <div
                    data-testid="live-stage-sharing-self"
                    className="relative rounded-xl overflow-hidden bg-emerald-900/40 aspect-video border border-emerald-500/40 flex flex-col items-center justify-center text-center p-4"
                >
                    <MonitorUp className="w-10 h-10 text-emerald-300 mb-2" />
                    <div className="text-sm font-semibold text-emerald-100">You are sharing your screen</div>
                    <div className="text-[11px] text-emerald-200/80 mt-1">
                        {remotePeer ? `${remotePeer.name} can see your screen.` : "The other participant will see your screen when they join."}
                    </div>
                </div>
                {remotePeer && (
                    <SmallRemoteCamera peer={remotePeer} videoOn={remoteVideoOn} audioOn={remoteAudioOn} />
                )}
            </div>
        );
    }
    // ---- No remote peer in the room yet
    if (!remotePeer) {
        return (
            <div
                data-testid="live-stage-waiting"
                className="relative rounded-xl overflow-hidden bg-black aspect-video border border-white/10 flex flex-col items-center justify-center text-center p-4"
            >
                <UserCircle2 className="w-12 h-12 text-slate-500 mb-2" />
                <div className="text-sm font-semibold text-slate-300">
                    Waiting for {isTutor ? "student / parent" : "tutor"} to join…
                </div>
                <div className="text-[11px] text-slate-500 mt-1">You are already connected. They'll appear here.</div>
            </div>
        );
    }
    // ---- Normal remote camera stage
    return (
        <RemoteCameraStage peer={remotePeer} videoOn={remoteVideoOn} audioOn={remoteAudioOn} />
    );
}

function RemoteCameraStage({ peer, videoOn, audioOn }) {
    const { videoRef } = useVideo({ trackId: peer.videoTrack });
    return (
        <div
            className="relative rounded-xl overflow-hidden bg-black aspect-video border border-cyan-500/40"
            data-testid={`live-remote-stage-${peer.id}`}
            data-peer-id={peer.id}
            data-peer-role={peer.roleName || ""}
        >
            {videoOn ? (
                // IMPORTANT: NOT muted — this is the remote peer's track; the SDK
                // attaches the remote MediaStream here via useVideo(trackId). The
                // SDK handles remote audio playback separately via its audio sink,
                // so this <video> element's audio stays effectively silent for us,
                // but we never mute it in markup because screen-share tracks can
                // sometimes carry their own in-track audio.
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                />
            ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                    <VideoOff className="w-10 h-10 mb-2" />
                    <div className="text-xs">{peer.name}'s camera is off</div>
                </div>
            )}
            <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-cyan-500/80 text-[10px] text-white font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                Live · {peer.roleName || "remote"}
            </div>
            <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-[11px] text-white/95">
                <span className="bg-black/60 px-1.5 py-0.5 rounded flex items-center gap-1">
                    {peer.name}
                </span>
                <span className="bg-black/60 px-1.5 py-0.5 rounded flex items-center gap-1">
                    {audioOn ? <Mic className="w-3 h-3" /> : <MicOff className="w-3 h-3 text-rose-300" />}
                    {videoOn ? <Video className="w-3 h-3" /> : <VideoOff className="w-3 h-3 text-rose-300" />}
                </span>
            </div>
        </div>
    );
}

function ScreenShareView({ trackId, peerName }) {
    const { videoRef } = useVideo({ trackId });
    return (
        <div
            className="relative rounded-xl overflow-hidden bg-black aspect-video border border-amber-500/60"
            data-testid={`live-screenshare-${trackId}`}
        >
            <video ref={videoRef} autoPlay playsInline className="w-full h-full object-contain bg-black" />
            <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-amber-500/90 text-[10px] text-black font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                <MonitorUp className="w-3 h-3" /> {peerName}'s screen
            </div>
        </div>
    );
}

function SmallRemoteCamera({ peer, videoOn, audioOn }) {
    const { videoRef } = useVideo({ trackId: peer.videoTrack });
    return (
        <div
            className="relative rounded-lg overflow-hidden bg-black aspect-video border border-white/15"
            data-testid={`live-remote-small-${peer.id}`}
            data-peer-id={peer.id}
        >
            {videoOn ? (
                <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
            ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">
                    <VideoOff className="w-5 h-5" />
                </div>
            )}
            <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between text-[10px] text-white/90">
                <span className="bg-black/60 px-1 py-0.5 rounded">{peer.name}</span>
                <span className="bg-black/60 px-1 py-0.5 rounded flex items-center gap-0.5">
                    {audioOn ? <Mic className="w-2.5 h-2.5" /> : <MicOff className="w-2.5 h-2.5 text-rose-300" />}
                </span>
            </div>
        </div>
    );
}

// SelfPreviewTile — the LOCAL participant's own camera, muted (no echo), mirrored.
// Clearly labelled "You" so no participant confuses their own feed for the remote.
function SelfPreviewTile({ peer, isTutor }) {
    const { videoRef } = useVideo({ trackId: peer.videoTrack });
    const videoOn = !!peer?.videoTrack?.enabled;
    const audioOn = !!peer?.audioTrack?.enabled;
    return (
        <div
            className="relative rounded-lg overflow-hidden bg-black aspect-video border border-white/20"
            data-testid="live-self-preview"
            data-peer-id={peer.id}
        >
            {videoOn ? (
                <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                    style={{ transform: "scaleX(-1)" }}
                />
            ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-500">
                    <VideoOff className="w-7 h-7 mb-1" />
                    <div className="text-[11px]">Camera off</div>
                </div>
            )}
            <div className="absolute top-1 left-1 inline-flex items-center gap-1 bg-slate-900/80 text-[10px] text-white font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">
                You · {isTutor ? "tutor" : "student / parent"}
            </div>
            <div className="absolute bottom-1 right-1 inline-flex items-center gap-0.5 bg-black/60 text-[10px] text-white px-1 py-0.5 rounded">
                {audioOn ? <Mic className="w-2.5 h-2.5" /> : <MicOff className="w-2.5 h-2.5 text-rose-300" />}
                {videoOn ? <Video className="w-2.5 h-2.5" /> : <VideoOff className="w-2.5 h-2.5 text-rose-300" />}
            </div>
        </div>
    );
}

function ConnectionChip({ status }) {
    const map = {
        connected: {
            cls: "bg-emerald-500/20 text-emerald-200",
            icon: <Wifi className="w-3.5 h-3.5" />,
            label: "Connected",
        },
        connecting: {
            cls: "bg-amber-500/20 text-amber-200",
            icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
            label: "Connecting",
        },
        disconnected: {
            cls: "bg-rose-500/20 text-rose-200",
            icon: <WifiOff className="w-3.5 h-3.5" />,
            label: "Offline",
        },
    };
    const m = map[status] || map.disconnected;
    return (
        <span
            data-testid="live-connection-chip"
            data-status={status}
            className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-1 ${m.cls}`}
        >
            {m.icon}
            <span className="hidden sm:inline">{m.label}</span>
        </span>
    );
}

function IconBtn({ onClick, on, onIcon, offIcon, label, testid, disabled }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            data-cm-live-ctl
            aria-label={label}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2.5 rounded-full transition-colors disabled:opacity-40 ${
                on ? "bg-white/10 hover:bg-white/15 text-white" : "bg-rose-500/20 hover:bg-rose-500/30 text-rose-100"
            }`}
        >
            {on ? onIcon : offIcon}
            <span className="hidden sm:inline">{label}</span>
        </button>
    );
}

function formatDuration(sec) {
    const s = Math.max(0, Math.floor(sec));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    return h > 0
        ? `${h}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`
        : `${m}:${String(r).padStart(2, "0")}`;
}

function FullScreenSpinner({ label }) {
    return (
        <div className="min-h-screen bg-[#0B1220] text-white flex items-center justify-center flex-col gap-2">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
            <div className="text-sm text-slate-300">{label}</div>
        </div>
    );
}

function FallbackNotice({ message, onBack, isTutor, onSubmitReport }) {
    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 classroom-mobile">
            <div className="max-w-md w-full text-center bg-white border border-amber-200 rounded-2xl p-6 shadow-sm">
                <AlertTriangle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                <h1 className="text-lg font-heading font-bold text-[#0F172A]">Classroom not available yet</h1>
                <p className="text-sm text-slate-600 mt-2">{message}</p>
                <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-center">
                    <button
                        onClick={onBack}
                        data-testid="live-fallback-back"
                        className="inline-flex items-center justify-center gap-1.5 bg-[#0F172A] text-white font-semibold text-sm px-4 py-2 rounded-lg hover:bg-[#1E293B]"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to portal
                    </button>
                    {isTutor && (
                        <button
                            onClick={onSubmitReport}
                            data-testid="live-fallback-submit-report"
                            className="inline-flex items-center justify-center gap-1.5 bg-rose-600 text-white font-semibold text-sm px-4 py-2 rounded-lg hover:bg-rose-700"
                        >
                            <ClipboardCheck className="w-4 h-4" /> Submit class report
                        </button>
                    )}
                </div>
                {isTutor && (
                    <p className="text-[11px] text-slate-500 mt-3">
                        If you ran the class over a phone / offline meeting, submit the report here to mark attendance and complete the session.
                    </p>
                )}
            </div>
        </div>
    );
}

export default function LiveClassroom() {
    return (
        <HMSRoomProvider>
            <LiveClassroomInner />
        </HMSRoomProvider>
    );
}

// ---------------------------------------------------- Class Report Modal
// V158.1 — Tutor's post-class report submitted at End Class. Attendance
// here drives the assignment counter server-side (`present`/`late`/`partial`
// count; `absent`/`cancelled` do NOT). Understanding + remarks feed the
// parent portal's session-history view.
const ATTENDANCE_OPTIONS = [
    { value: "present", label: "Present" },
    { value: "late", label: "Late" },
    { value: "partial", label: "Partial (short session)" },
    { value: "absent", label: "Absent" },
    { value: "cancelled", label: "Cancelled" },
];
const UNDERSTANDING_OPTIONS = [
    { value: "excellent", label: "Excellent" },
    { value: "good", label: "Good" },
    { value: "average", label: "Average" },
    { value: "needs_improvement", label: "Needs improvement" },
];

function ClassReportModal({ elapsedSec, submitting, onCancel, onSubmit }) {
    const [studentAttendance, setStudentAttendance] = useState("present");
    const [topicCovered, setTopicCovered] = useState("");
    const [homework, setHomework] = useState("");
    const [understanding, setUnderstanding] = useState("good");
    const [tutorRemarks, setTutorRemarks] = useState("");
    const [nextTopic, setNextTopic] = useState("");

    const canSubmit = topicCovered.trim().length > 0 && !submitting;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onSubmit({
            student_attendance: studentAttendance,
            tutor_attendance: "present",
            topic_covered: topicCovered.trim(),
            homework: homework.trim() || null,
            understanding,
            tutor_remarks: tutorRemarks.trim() || null,
            next_topic: nextTopic.trim() || null,
        });
    };

    return (
        <div
            className="fixed inset-0 bg-black/60 z-[100] flex items-start sm:items-center justify-center overflow-y-auto p-0 sm:p-4 classroom-mobile"
            data-testid="live-report-modal"
        >
            <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl shadow-2xl min-h-screen sm:min-h-0 sm:max-h-[92vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between z-10">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-cyan-600 font-bold flex items-center gap-1.5">
                            <ClipboardCheck className="w-3.5 h-3.5" /> End of Class Report
                        </div>
                        <div className="text-lg font-heading font-bold text-[#0F172A] mt-0.5">Wrap up this session</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                            Elapsed on your side: <span className="font-semibold text-slate-700">{formatDuration(elapsedSec)}</span>
                            {" · "}Final elapsed comes from the server.
                        </div>
                    </div>
                    <button
                        onClick={onCancel}
                        disabled={submitting}
                        data-testid="live-report-cancel"
                        data-cm-compact
                        aria-label="Cancel"
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-600 disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
                    {/* Attendance */}
                    <FieldGroup label="Student attendance" required>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
                            {ATTENDANCE_OPTIONS.map((o) => (
                                <button
                                    type="button"
                                    key={o.value}
                                    onClick={() => setStudentAttendance(o.value)}
                                    data-testid={`live-report-attendance-${o.value}`}
                                    className={`px-2 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
                                        studentAttendance === o.value
                                            ? "bg-cyan-50 border-cyan-400 text-cyan-800"
                                            : "border-slate-200 text-slate-600 hover:border-slate-300"
                                    }`}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                        {studentAttendance === "absent" || studentAttendance === "cancelled" ? (
                            <p className="text-[11px] text-amber-700 mt-1">This session will NOT count toward the package.</p>
                        ) : (
                            <p className="text-[11px] text-emerald-700 mt-1">This session will count as completed in the parent's package.</p>
                        )}
                    </FieldGroup>

                    {/* Topic covered */}
                    <FieldGroup label="Topic covered" required>
                        <textarea
                            value={topicCovered}
                            onChange={(e) => setTopicCovered(e.target.value)}
                            data-testid="live-report-topic"
                            rows={2}
                            placeholder="What did you teach today? (e.g. Trigonometric identities, chapter 3)"
                            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
                        />
                    </FieldGroup>

                    {/* Understanding */}
                    <FieldGroup label="Student's understanding">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                            {UNDERSTANDING_OPTIONS.map((o) => (
                                <button
                                    type="button"
                                    key={o.value}
                                    onClick={() => setUnderstanding(o.value)}
                                    data-testid={`live-report-understanding-${o.value}`}
                                    className={`px-2 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
                                        understanding === o.value
                                            ? "bg-cyan-50 border-cyan-400 text-cyan-800"
                                            : "border-slate-200 text-slate-600 hover:border-slate-300"
                                    }`}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    </FieldGroup>

                    {/* Homework */}
                    <FieldGroup label="Homework assigned">
                        <textarea
                            value={homework}
                            onChange={(e) => setHomework(e.target.value)}
                            data-testid="live-report-homework"
                            rows={2}
                            placeholder="Optional — questions, page numbers, deadline…"
                            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
                        />
                    </FieldGroup>

                    {/* Tutor remarks */}
                    <FieldGroup label="Remarks for parent">
                        <textarea
                            value={tutorRemarks}
                            onChange={(e) => setTutorRemarks(e.target.value)}
                            data-testid="live-report-remarks"
                            rows={2}
                            placeholder="Optional — how did the session go? Any suggestions for the parent?"
                            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
                        />
                    </FieldGroup>

                    {/* Next topic */}
                    <FieldGroup label="Planned next topic">
                        <input
                            value={nextTopic}
                            onChange={(e) => setNextTopic(e.target.value)}
                            data-testid="live-report-next-topic"
                            placeholder="Optional — what will you cover next class?"
                            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
                        />
                    </FieldGroup>

                    <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-[11px] text-slate-600">
                        Submitting will end the class for everyone, save the whiteboard snapshot,
                        update attendance, and (for non-absent students) count this as one completed session.
                    </div>

                    {/* Sticky footer */}
                    <div className="sticky bottom-0 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-white border-t border-slate-200 flex justify-end gap-2 mt-2">
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={submitting}
                            data-testid="live-report-back"
                            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50"
                        >
                            Back to class
                        </button>
                        <button
                            type="submit"
                            disabled={!canSubmit}
                            data-testid="live-report-submit"
                            data-cm-primary
                            className="inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-semibold"
                        >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
                            Submit &amp; end class
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function FieldGroup({ label, required, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1.5">
                {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
            </div>
            {children}
        </div>
    );
}
